// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useEffect, type ReactNode } from 'react'
import { CheckCircle2, AlertCircle, Info, TriangleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { useToastState, type Toast } from '@/lib/hooks/useToast'

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: TriangleAlert,
}

const classes = {
  success: 'border-success/30 bg-success-subtle text-success',
  error: 'border-danger/30 bg-danger-subtle text-danger',
  info: 'border-info/30 bg-info-subtle text-info',
  warning: 'border-warning/30 bg-warning-subtle text-warning',
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const Icon = icons[toast.variant]
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 shadow-lg min-w-[280px] max-w-sm animate-slide-in-right',
        classes[toast.variant]
      )}
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      <button onClick={onDismiss} className="shrink-0 opacity-60 hover:opacity-100 transition-opacity">
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const { toasts, addListener, dismiss } = useToastState()

  useEffect(() => {
    return addListener()
  }, [addListener])

  return (
    <>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2" aria-live="polite">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </>
  )
}
